import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Award, Lock, Search } from "lucide-react";

export default function Directory() {
  const { user } = useAuth();
  const [members, setMembers] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/members/directory")
      .then(r => setMembers(r.data))
      .catch(e => setError(e.response?.status === 403 ? "locked" : "error"));
  }, []);

  const filtered = useMemo(() => {
    if (!members) return [];
    const s = q.trim().toLowerCase();
    if (!s) return members;
    return members.filter(m =>
      (m.name || "").toLowerCase().includes(s) ||
      (m.agency || "").toLowerCase().includes(s) ||
      String(m.cohort_year || "").includes(s)
    );
  }, [members, q]);

  if (error === "locked") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="directory-locked">
        <Lock className="w-12 h-12 mx-auto text-[#0A192F]" />
        <div className="label-eyebrow mt-6 text-[#C5A059]">Founders' Circle exclusive</div>
        <h1 className="mt-3 font-display text-4xl text-[#0A192F]">The Members Directory is private.</h1>
        <p className="mt-4 text-slate-600">Join the Founders' Circle to unlock the full searchable directory of Association members.</p>
        <Link to="/founders"><Button className="mt-8 bg-[#C5A059] text-[#0A192F] hover:bg-[#B38F48] rounded-md h-12 px-8 font-semibold">Join the Founders' Circle — $99/yr</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="directory-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="label-eyebrow text-[#C5A059] flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Founders' Circle</div>
          <h1 className="mt-2 font-display text-4xl text-[#0A192F]">Members Directory</h1>
          <p className="mt-2 text-slate-600 text-sm">{members ? `${members.length} members of the Association.` : "Loading…"}</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, agency, cohort…" className="pl-9 h-11" data-testid="directory-search" />
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(m => (
          <Link to={`/u/${m.id}`} key={m.id} className="card-flat p-5 hover:-translate-y-0.5 hover:shadow-md transition-all flex gap-4 items-start" data-testid={`directory-card-${m.id}`}>
            <div className="w-11 h-11 rounded-full bg-[#002855] text-[#C5A059] flex items-center justify-center font-display text-lg">{m.name?.[0]}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="font-display text-lg text-[#0A192F] truncate">{m.name}</div>
                {m.founders_member && <Award className="w-3.5 h-3.5 text-[#C5A059] shrink-0" title="Founders' Circle" />}
              </div>
              <div className="text-xs text-slate-500 truncate">{m.agency || "—"}</div>
              <div className="label-eyebrow mt-1 text-slate-400">Cohort {m.cohort_year || "—"}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
